import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Chip,
  LinearProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  Today as TodayIcon,
  CalendarMonth as CalendarMonthIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { attendantsService, AttendantStats, DashboardData } from '../services/attendantsService';

const AttendantsDashboard: React.FC = () => {
  // Estados
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    attendant_id: '',
  });
  const [attendants, setAttendants] = useState<{ id: string; name: string }[]>([]);

  // Carregar atendentes para o filtro
  useEffect(() => {
    const loadAttendants = async () => {
      try {
        const data = await attendantsService.getAll(true);
        setAttendants(data.map(a => ({ id: a.id, name: a.name })));
      } catch (error) {
        console.error('Erro ao carregar atendentes:', error);
      }
    };
    loadAttendants();
  }, []);

  // Carregar dashboard
  const loadDashboard = async () => {
    try {
      setLoading(true);
      const data = await attendantsService.getDashboard({
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        attendant_id: filters.attendant_id || undefined,
      });
      setDashboardData(data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  // Aplicar filtros
  const handleApplyFilters = () => {
    loadDashboard();
  };

  // Limpar filtros
  const handleClearFilters = () => {
    setFilters({
      date_from: '',
      date_to: '',
      attendant_id: '',
    });
    setTimeout(loadDashboard, 100);
  };

  // Calcular progresso
  const getProgressValue = (value: number, max: number) => {
    if (max === 0) return 0;
    return Math.min((value / max) * 100, 100);
  };

  // Encontrar o máximo para normalizar as barras
  const maxAssignments = dashboardData?.attendants.reduce(
    (max, a) => Math.max(max, a.total_assignments),
    0
  ) || 1;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          📊 Dashboard de Atendentes
        </Typography>
        <Tooltip title="Atualizar">
          <IconButton onClick={loadDashboard} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              label="Data Início"
              type="date"
              value={filters.date_from}
              onChange={(e) =>
                setFilters({ ...filters, date_from: e.target.value })
              }
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Data Fim"
              type="date"
              value={filters.date_to}
              onChange={(e) =>
                setFilters({ ...filters, date_to: e.target.value })
              }
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Atendente"
              select
              value={filters.attendant_id}
              onChange={(e) =>
                setFilters({ ...filters, attendant_id: e.target.value })
              }
              fullWidth
              size="small"
            >
              <MenuItem value="">Todos</MenuItem>
              {attendants.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                disabled={loading}
              >
                Filtrar
              </Button>
              <Button variant="outlined" onClick={handleClearFilters}>
                Limpar
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : dashboardData ? (
        <>
          {/* Cards de Resumo */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <PersonIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {dashboardData.totals.total_attendants}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Atendentes Ativos
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'success.main' }}>
                      <AssignmentIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {dashboardData.totals.total_assignments}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total de Atribuições
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'warning.main' }}>
                      <TodayIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {dashboardData.totals.assignments_today}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Hoje
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'info.main' }}>
                      <CalendarMonthIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {dashboardData.totals.assignments_this_month}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Este Mês
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Tabela de Atendentes */}
          <Paper>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">
                <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Desempenho por Atendente
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell>Atendente</TableCell>
                    <TableCell align="center">Total</TableCell>
                    <TableCell align="center">Hoje</TableCell>
                    <TableCell align="center">Semana</TableCell>
                    <TableCell align="center">Mês</TableCell>
                    <TableCell>Progresso</TableCell>
                    <TableCell>Última Atribuição</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dashboardData.attendants.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          Nenhum atendente encontrado
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    dashboardData.attendants.map((attendant) => (
                      <TableRow key={attendant.id} hover>
                        <TableCell>
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                          >
                            <Avatar
                              sx={{
                                bgcolor: attendant.color,
                                width: 36,
                                height: 36,
                              }}
                            >
                              {attendant.name.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography fontWeight="medium">
                                {attendant.name}
                              </Typography>
                              {attendant.email && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {attendant.email}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={attendant.total_assignments}
                            color="primary"
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={attendant.assignments_today}
                            color={
                              attendant.assignments_today > 0
                                ? 'success'
                                : 'default'
                            }
                            size="small"
                            variant={
                              attendant.assignments_today > 0
                                ? 'filled'
                                : 'outlined'
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          {attendant.assignments_this_week}
                        </TableCell>
                        <TableCell align="center">
                          {attendant.assignments_this_month}
                        </TableCell>
                        <TableCell sx={{ minWidth: 150 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ width: '100%', mr: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={getProgressValue(
                                  attendant.total_assignments,
                                  maxAssignments
                                )}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  bgcolor: 'grey.200',
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: attendant.color,
                                    borderRadius: 4,
                                  },
                                }}
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {Math.round(
                                getProgressValue(
                                  attendant.total_assignments,
                                  maxAssignments
                                )
                              )}
                              %
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {attendant.last_assignment_at ? (
                            <Typography variant="body2">
                              {new Date(
                                attendant.last_assignment_at
                              ).toLocaleString('pt-BR', {
                                day: '2-digit',
                                month: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Nunca
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Erro ao carregar dados. Tente novamente.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default AttendantsDashboard;
